import type { TypesenseSearchParams } from '~/utils/typesenseClient'
import {
  TYPO_PROFILES,
  typesenseTypoParams,
  type TypoProfile,
} from '~/utils/typesenseTypoTolerance'
import {
  EVENTS_RANKING,
  EVENTS_TEXT_MATCH_PARAMS,
  TEXT_MATCH_SORT,
  buildTypesenseBrowseGeoSort,
  buildTypesenseNearbyFirstSort,
  type GeoCoords,
} from '~/utils/typesenseGeo'

/**
 * How a text query searches the events collection — shared by the federated
 * search dropdown and the /search page (KFT-4145), so the same query returns
 * the same events in the same order on both. Each surface adds only what is
 * its own: the dropdown its facets and caps, /search its filters, pagination
 * and the other sort options.
 */

/**
 * Searchable fields in priority order, ported from Algolia's events
 * `searchableAttributes` (title, categories.name, tags, city, supplier,
 * subtitle). With `text_match_type: max_weight` the order is the ranking.
 * No `description`: it's non-indexed on the cluster, and Typesense rejects a
 * search that lists it.
 */
export const EVENTS_QUERY_BY =
  'title,categories.name,tags.name,city,supplier,subtitle'

/**
 * Cluster stopword sets (`/stopwords/<id>`): German/English filler words
 * ("bei", "was", "für", …) dropped from the query — Algolia's
 * `removeStopWords: de, en`. Named `<events collection>_stopwords`; only sets
 * listed here exist, and passing a missing set fails the whole request.
 */
export const EVENTS_STOPWORD_SETS: ReadonlySet<string> = new Set([
  'events_de_stopwords',
  'events_en_stopwords',
])

/** The stopwords param for an events collection, when its set exists. */
export const eventsStopwords = (
  collection: string
): Pick<TypesenseSearchParams, 'stopwords'> => {
  const set = `${collection}_stopwords`
  return EVENTS_STOPWORD_SETS.has(set) ? { stopwords: set } : {}
}

/**
 * Query text, fields and text-match settings for an events text search.
 * `text` is the query with constraint phrases already removed
 * (`parseSearchIntent`); empty means browse (`*`).
 */
export const buildEventsTextSearchParams = ({
  text,
  collection,
  typoProfile = TYPO_PROFILES.events,
}: {
  text: string
  collection: string
  typoProfile?: TypoProfile
}): Pick<
  TypesenseSearchParams,
  | 'q'
  | 'query_by'
  | 'num_typos'
  | 'min_len_1typo'
  | 'min_len_2typo'
  | 'text_match_type'
  | 'prioritize_token_position'
  | 'stopwords'
> => ({
  q: text.trim() || '*',
  query_by: EVENTS_QUERY_BY,
  ...typesenseTypoParams(EVENTS_QUERY_BY, typoProfile),
  ...EVENTS_TEXT_MATCH_PARAMS,
  ...eventsStopwords(collection),
})

/**
 * Relevance sort for an events search. With viewer coords and text: nearby
 * (offline) events, then online, then the rest — each group dated events
 * first, then by text match. With coords and no text (browsing /search and
 * /c/ pages): nearby dated events, then by distance bucket, then recent
 * bookings (`buildTypesenseBrowseGeoSort`). Without coords: Algolia's
 * text-first chain — text match, then dated events, then recent bookings.
 */
export const buildEventsRelevanceSort = (
  coords: GeoCoords | null | undefined,
  hasText: boolean
): string =>
  (hasText
    ? buildTypesenseNearbyFirstSort(
        coords,
        `has_events:desc,${TEXT_MATCH_SORT}`
      )
    : buildTypesenseBrowseGeoSort(coords)) ??
  `${TEXT_MATCH_SORT},${EVENTS_RANKING}`
